import { redirect } from "@tanstack/react-router";
import { can } from "@/lib/staff-roles";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Trash2, UserPlus } from "lucide-react";
import {
  STAFF_ROLES,
  getStaffing,
  removeStaffMember,
  saveStaffMember,
  setStaffShift,
  type StaffMember,
  type StaffRole,
  type StaffShift,
} from "@/lib/staffing.functions";

export const Route = createFileRoute("/_authenticated/admin/staffing")({
  beforeLoad: ({ context }) => {
    if (!can(context.staffRole, "viewStaffing")) {
      throw redirect({ to: "/admin/calendar" });
    }
  },
  head: () => ({
    meta: [
      { title: "Staffing — Coffman Cove Lodging Admin" },
      { name: "description", content: "Assign hospitality, dock and fish processing staff to each day." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminStaffing,
});

const DAY = 86_400_000;

function iso(date: Date) {
  return date.toISOString().slice(0, 10);
}

/** Seven days beginning on the most recent Friday, matching the lodge week. */
function weekStart(from: Date) {
  const d = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()));
  const back = (d.getUTCDay() - 5 + 7) % 7;
  return new Date(d.getTime() - back * DAY);
}

function dayLabel(date: string) {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "numeric",
    day: "numeric",
    timeZone: "UTC",
  });
}

function AdminStaffing() {
  const { staffRole } = Route.useRouteContext();
  const canEdit = can(staffRole, "manageStaffing");
  const [anchor, setAnchor] = useState(() => iso(weekStart(new Date())));
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => iso(new Date(Date.parse(`${anchor}T00:00:00Z`) + i * DAY))),
    [anchor],
  );

  const load = useServerFn(getStaffing);
  const save = useServerFn(saveStaffMember);
  const remove = useServerFn(removeStaffMember);
  const shift = useServerFn(setStaffShift);
  const qc = useQueryClient();

  const queryKey = ["admin-staffing", anchor];
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => load({ data: { start: days[0], end: days[6] } }),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["admin-staffing"] });
  const addMutation = useMutation({ mutationFn: save, onSuccess: invalidate });
  const removeMutation = useMutation({ mutationFn: remove, onSuccess: invalidate });
  const shiftMutation = useMutation({ mutationFn: shift, onSuccess: invalidate });

  const [name, setName] = useState("");
  const [role, setRole] = useState<StaffRole>("hospitality");
  const [phone, setPhone] = useState("");

  const members = (data?.members ?? []) as StaffMember[];
  const shifts = (data?.shifts ?? []) as StaffShift[];
  const assigned = useMemo(() => {
    const set = new Set<string>();
    for (const s of shifts) set.add(`${s.staff_id}|${s.shift_date}|${s.role}`);
    return set;
  }, [shifts]);

  function shiftWeek(weeks: number) {
    setAnchor(iso(new Date(Date.parse(`${anchor}T00:00:00Z`) + weeks * 7 * DAY)));
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl">Staffing</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Tick the days each person is working. Weeks run Friday to Friday.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => shiftWeek(-1)}
            className="rounded-full border border-border px-3 py-1.5 text-sm"
          >
            Previous
          </button>
          <span className="text-sm font-semibold">
            {dayLabel(days[0])} – {dayLabel(days[6])}
          </span>
          <button
            type="button"
            onClick={() => shiftWeek(1)}
            className="rounded-full border border-border px-3 py-1.5 text-sm"
          >
            Next
          </button>
        </div>
      </header>

      {!canEdit ? (
        <p className="mt-6 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
          You can see who is working each day. Only a manager can change the schedule.
        </p>
      ) : null}

      {canEdit ? (
      <form
        className="mt-6 flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          addMutation.mutate(
            { data: { name: name.trim(), role, phone: phone.trim() || null } },
            { onSuccess: () => { setName(""); setPhone(""); } },
          );
        }}
      >
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-md border border-border bg-background px-3 py-2"
            placeholder="Add someone"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Crew</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as StaffRole)}
            className="rounded-md border border-border bg-background px-3 py-2"
          >
            {STAFF_ROLES.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Phone (optional)</span>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="rounded-md border border-border bg-background px-3 py-2"
            placeholder="(907) 555-0100"
          />
        </label>
        <button
          type="submit"
          disabled={addMutation.isPending}
          className="inline-flex items-center gap-2 rounded-full bg-[var(--color-ember)] px-4 py-2 text-sm font-semibold text-[var(--color-ember-foreground)] disabled:opacity-60"
        >
          <UserPlus size={14} aria-hidden />
          Add to crew
        </button>
      </form>
      ) : null}

      {isLoading ? <p className="mt-6 text-sm text-muted-foreground">Loading the schedule…</p> : null}
      {error ? <p className="mt-6 text-sm text-destructive">Could not load the schedule.</p> : null}

      {!isLoading && !error
        ? STAFF_ROLES.map((crew) => {
            const crewMembers = members.filter((m) => m.role === crew.id && m.active);
            return (
              <section key={crew.id} className="mt-8 rounded-xl border border-border bg-card p-4">
                <h2 className="font-display text-lg">{crew.label}</h2>
                {crewMembers.length === 0 ? (
                  <p className="mt-2 text-sm text-muted-foreground">Nobody on this crew yet.</p>
                ) : (
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full min-w-[640px] text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                          <th className="py-2 pr-3">Person</th>
                          {days.map((d) => (
                            <th key={d} className="px-2 py-2 text-center font-semibold">
                              {dayLabel(d)}
                            </th>
                          ))}
                          <th className="py-2" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {crewMembers.map((m) => (
                          <tr key={m.id}>
                            <td className="py-2 pr-3">
                              <span className="font-medium">{m.name}</span>
                              {m.phone ? (
                                <span className="block text-xs text-muted-foreground">{m.phone}</span>
                              ) : null}
                            </td>
                            {days.map((d) => {
                              const on = assigned.has(`${m.id}|${d}|${crew.id}`);
                              return (
                                <td key={d} className="px-2 py-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={on}
                                    disabled={!canEdit}
                                    aria-label={`${m.name} working ${dayLabel(d)}`}
                                    onChange={() => {
                                      if (!canEdit) return;
                                      shiftMutation.mutate({
                                        data: {
                                          staff_id: m.id,
                                          shift_date: d,
                                          role: crew.id,
                                          assigned: !on,
                                        },
                                      });
                                    }}
                                    className="h-4 w-4 accent-[var(--color-ember)] disabled:opacity-60"
                                  />
                                </td>
                              );
                            })}
                            <td className="py-2 text-right">
                              {canEdit ? (
                              <button
                                type="button"
                                aria-label={`Remove ${m.name}`}
                                onClick={() => {
                                  if (window.confirm(`Remove ${m.name} from the crew list?`)) {
                                    removeMutation.mutate({ data: { id: m.id } });
                                  }
                                }}
                                className="text-muted-foreground transition-colors hover:text-destructive"
                              >
                                <Trash2 size={15} aria-hidden />
                              </button>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  {days.map((d) => {
                    const covering = crewMembers.filter((m) => assigned.has(`${m.id}|${d}|${crew.id}`));
                    return (
                      <div key={d} className="rounded-lg border border-border/70 px-3 py-2 text-sm">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">
                          {dayLabel(d)}
                        </span>
                        <p className={covering.length === 0 ? "text-destructive" : ""}>
                          {covering.length === 0 ? "Nobody covering" : covering.map((m) => m.name).join(", ")}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })
        : null}
    </div>
  );
}

export default AdminStaffing;
